import React, { Fragment, useState } from "react";
import { Autocomplete, Box, Chip, Divider, ListItemText, MenuItem, Popover, Stack, TextField, Typography } from "@mui/material";
import { GenericIconButton } from "./GenericIconButton";
import { CalendarBlank, CaretDown, CaretUp, Divide, Eye, EyeSlash, MagnifyingGlass, X } from "@phosphor-icons/react";
import { DateCalendar, DateField } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useTheme } from "@emotion/react";
import { COUNTRY_WITH_CODES } from "../../enums/countries";
import { MAX_CHAR_BIO } from "../../enums/generalEnums";
import { GenericAvatar } from "./GenericAvatar";

export const SearchField = ({ placeholder = 'Search', width = 400, handleChange, handleClear, value, disableSearchButton = true, searchButtonAction, ...others }) => {
  const theme = useTheme();

  return (
    <Box sx={{
      backgroundColor: '#FFF',
      borderRadius: '7px',
      border: '1px solid #C4CDD5',
      width: '100%'
    }}>
      <TextField
        {...others}
        value={value}
        fullWidth
        onChange={(event) => handleChange(event.target.value)}
        size="small"
        placeholder={placeholder}
        sx={{
          width: width,
          '& fieldset': {
            border: 'none'
          }
        }}
        slotProps={{
          input: {
            endAdornment: (
              <Stack direction={'row'} alignItems={'center'}>
                {
                  (value && !disableSearchButton) && <GenericIconButton
                    color={'grey.dark'}
                    disabled={disableSearchButton}
                    icon={<X size={20} />}
                    tooltip={'Clear'}
                    onClick={() => handleClear()}
                  />
                }
                <GenericIconButton
                  disabled={disableSearchButton}
                  onClick={() => searchButtonAction()}
                  icon={<MagnifyingGlass size={20} color={theme.palette.text.light} />}
                  tooltip={'Search'}
                />
              </Stack>
            ),
          },
        }}
      />
    </Box>
  );
}

export const FormTextField = (props) => {
  const {
    value = "",
    placeholder,
    label,
    isRequired = true,
    helperText,
    error,
    handleChange,
    isSelect = false,
    children,
    multiline = false,
    rows = 5,
    color = 'text.light',
    type = 'text',
    ...others
  } = props;

  const handleKeyDown = (event) => {
    if (type === "number" && (event.key === '-' || event.key === 'e')) {
      event.preventDefault();
    }
  };

  return (
    <Stack gap={1}>
      <Typography color={color} fontWeight={700}>{label} {isRequired && <span style={{ color: '#F96D82' }}>*</span>}</Typography>
      <TextField
        {...others}
        type={type}
        multiline={multiline}
        rows={rows}
        select={isSelect}
        value={value}
        size="small"
        placeholder={placeholder}
        error={error}
        helperText={helperText}
        onKeyDown={handleKeyDown}
        onChange={(event) => handleChange(event.target.value)}
      >
        {
          isSelect && (
            <MenuItem value="" disabled>
              {placeholder}
            </MenuItem>
          )
        }
        {children}
      </TextField>
    </Stack>
  )
}

export const PasswordField = ({ value = "", placeholder, label, isRequired = true, helperText, error, handleChange, ...others }) => {
  const theme = useTheme();
  const [hidden, setHidden] = useState(true);
  const iconColor = theme.palette.text.light;;

  return (
    <Stack gap={1}>
      <Typography color="text.light" fontWeight={700}>{label} {isRequired && <span style={{ color: '#F96D82' }}>*</span>}</Typography>
      <TextField
        {...others}
        value={value}
        size="small"
        type={hidden ? "password" : 'text'}
        placeholder={placeholder}
        error={error}
        helperText={helperText}
        onChange={(event) => handleChange(event.target.value)}
        slotProps={{
          input: {
            endAdornment: (
              <GenericIconButton
                icon={hidden ? <Eye size={24} color={iconColor} /> : <EyeSlash size={24} color={iconColor} />}
                tooltip={`${hidden ? 'Show' : 'Hide'} Password`}
                onClick={() => setHidden((prev) => !prev)}
              />
            ),
          },
        }}
      />
    </Stack>
  )
}

export const DesktopDatePickerField = ({ label, disabled = false, isRequired = true, helperText, error, value = "", handleChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpenDateSelector = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleChangeDate = (newValue) => {
    handleChange(newValue.format('YYYY-MM-DD'));
    setAnchorEl(null);
  }

  return (
    <Stack gap={1}>
      <Typography color="text.light" fontWeight={700}>{label} {isRequired && <span style={{ color: '#F96D82' }}>*</span>}</Typography>
      <Box sx={{ border: `1px solid ${error ? 'red' : '#C4CDD5'}`, backgroundColor: disabled ? '#F4F6F8' : '#FFF', borderRadius: '7px', pr: 1.2 }}>
        <Stack direction={'row'} justifyContent={'space-between'}>
          <DateField
            disabled={disabled}
            value={dayjs(value)}
            onChange={handleChangeDate}
            size="small"
            sx={{
              '& fieldset': {
                border: 'none',
                padding: '0px',
              },
              '.MuiOutlinedInput-root': {
                background: disabled && '#F4F6F8',
                height: '35px'
              }
            }}
          />
          <GenericIconButton
            color={disabled ? 'grey.dark' : 'text.light'}
            disabled={disabled}
            icon={<CalendarBlank size={24} />}
            onClick={handleOpenDateSelector}
          />
        </Stack>
      </Box>
      {helperText && <Typography variant="caption" color="error" ml={2}>{helperText}</Typography>}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <DateCalendar value={value ? dayjs(value) : null} onChange={handleChangeDate} />
      </Popover>
    </Stack>
  )
};

export const FormAutoComplete = (props) => {
  const {
    options,
    value = "",
    handleChange,
    placeholder,
    error,
    helperText,
    label,
    isRequired = true,
    disabled = false,
    color = "text.light",
    ...others
  } = props;

  return (
    <Stack gap={1}>
      <Typography color={color} fontWeight={700}>{label} {isRequired && <span style={{ color: '#F96D82' }}>*</span>}</Typography>
      <Autocomplete
        {...others}
        disabled={disabled}
        options={options}
        value={value}
        onChange={(event, newValue) => {
          handleChange(newValue);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder={placeholder}
            variant="outlined"
            error={error}
            helperText={helperText}
            sx={{
              '.MuiOutlinedInput-root': {
                background: disabled && '#F4F6F8'
              }
            }}
          />
        )}
      />
    </Stack>
  )
}

export const FormAutoCompleteUsers = (props) => {
  const {
    options,
    value = "",
    handleChange,
    placeholder,
    error,
    helperText,
    label,
    isRequired = true,
    disabled = false,
    color = "text.light",
    ...others
  } = props;

  return (
    <Stack gap={1}>
      <Typography color={color} fontWeight={700}>
        {label} {isRequired && <span style={{ color: '#F96D82' }}>*</span>}
      </Typography>
      <Autocomplete
        multiple
        {...others}
        disabled={disabled}
        options={options}
        getOptionLabel={(option) => option.name}
        value={value}
        onChange={(event, newValue) => {
          handleChange(newValue);
        }}
        renderTags={(selected, getTagProps) =>
          selected.map((option, index) => (
            <Chip
              key={`recipient-key-${index}`}
              label={option?.name}
              avatar={
                <Box>
                  <GenericAvatar location={option?.profilePicture} size={'24px'} />
                </Box>
              }
              variant="outlined"
              deleteIcon={
                <GenericIconButton
                  color={'grey.dark'}
                  icon={<X size={20} />}
                  tooltip={'Clear'}
                />
              }
              {...getTagProps({ index })}
            />
          ))
        }
        renderOption={(props, option) => (
          <Fragment key={`recipient-key-${option.email}`}>
            <Box
              component="li"
              {...props}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                px: 0,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1.5,
                  py: 1,
                  width: '100%',
                }}
              >
                <GenericAvatar location={option?.profilePicture} size="32px" />
                <Box display="flex" flexDirection="column" justifyContent="center">
                  <Typography fontWeight={600} fontSize="0.9rem">
                    {option.name}
                  </Typography>
                  <Typography fontSize="0.8rem" color="text.secondary">
                    {option.email}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Divider sx={{ width: '100%' }} />
          </Fragment>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder={placeholder}
            variant="outlined"
            error={error}
            helperText={helperText}
            sx={{
              '.MuiOutlinedInput-root': {
                background: disabled && '#F4F6F8',
              },
            }}
          />
        )}
      />
    </Stack>
  );
};

export const IconFormTextField = (props) => {
  const {
    value = "",
    placeholder,
    label,
    isRequired = true,
    helperText, error,
    handleChange,
    icon,
    color = 'text.light'
  } = props;

  return (
    <Stack gap={1}>
      <Typography color={color} fontWeight={700}>{label} {isRequired && <span style={{ color: '#F96D82' }}>*</span>}</Typography>
      <TextField
        value={value}
        size="small"
        placeholder={placeholder}
        error={error}
        helperText={helperText}
        onChange={(event) => handleChange(event.target.value)}
        slotProps={{
          input: {
            startAdornment: <Box padding={'5px 10px 0 0'}>{icon}</Box>,
          },
        }}
      />
    </Stack>
  )
};

export const GenericPhoneNumberField = (props) => {
  const {
    value,
    error,
    label,
    isRequired = true,
    helperText,
    handleChange
  } = props;

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCode, setSelectedCode] = useState('+00');

  const handleOpenPopover = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSelectCode = (code) => {
    setSelectedCode(code);
    setAnchorEl(null);
  }

  return (
    <Stack gap={1}>
      <Typography color="text.light" fontWeight={700}>{label} {isRequired && <span style={{ color: '#F96D82' }}>*</span>}</Typography>
      <TextField
        value={value}
        type="number"
        size="small"
        error={error}
        helperText={helperText}
        onChange={(event) => handleChange(event.target.value)}
        slotProps={{
          input: {
            startAdornment: <Box paddingRight={'10px'}>
              <Stack direction={'row'} alignItems={'center'} gap={1} onClick={handleOpenPopover} sx={{ cursor: 'pointer' }}>
                <Typography color="text.light" fontWeight={700}>{selectedCode}</Typography>
                {anchorEl ? <CaretUp size={16} weight="bold" /> : <CaretDown size={16} weight="bold" />}
              </Stack>
            </Box>
          }
        }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPaper-root': {
            width: 200,
            height: 200,
          },
        }}
      >
        {
          COUNTRY_WITH_CODES?.map((country, index) => (
            <MenuItem selected={selectedCode === country?.code} key={`country-key-${index}`} value={country?.code} onClick={() => handleSelectCode(country?.code)}>
              <ListItemText>
                <Typography fontWeight={700} noWrap >
                  {country?.name}
                </Typography>
              </ListItemText>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {country?.code}
              </Typography>
            </MenuItem>
          ))
        }
      </Popover>
    </Stack>
  )
};

export const GenericTextArea = (props) => {
  const {
    handleChange,
    error,
    helperText,
    value,
    placeholder,
    label,
    isRequired,
    color,
    labelVariant,
    rows = 7,
    ...others
  } = props;

  const handleFieldChange = (value) => {
    if (value.length <= MAX_CHAR_BIO) {
      handleChange(value);
    }
  };

  const maxCharacters = MAX_CHAR_BIO - (value?.length ?? 0);

  return (
    <Fragment>
      <Stack gap={1} {...others}>
        <Typography color={color} variant={labelVariant} fontWeight={700}>{label} {isRequired && <span style={{ color: '#F96D82' }}>*</span>}</Typography>
        <TextField
          multiline={true}
          rows={rows}
          value={value}
          size="small"
          placeholder={placeholder}
          onChange={(event) => handleFieldChange(event.target.value)}
        />
      </Stack>
      <Stack direction={'row'} justifyContent={'space-between'}>
        <Typography fontSize={'10px'} color={maxCharacters <= 50 ? "error" : "grey.dark"} mt={1}>{`${maxCharacters} characters left`}</Typography>
        {error && <Typography fontSize={'10px'} variant="caption" color="error" mt={1}>{helperText ?? `${label} is required`}</Typography>}
      </Stack>
    </Fragment>
  )
};